import { useInputField } from '@chilipiper/form'
import { ActiveRange, WorkingHours } from '@chilipiper/api-client-user-availability'
import { Box, Flex, ScreenReaderOnly, Text } from '@chilipiper/design-system'
import React, { useId } from 'react'
import * as R from 'remeda'
import { Field as FinalField } from 'react-final-form'
import { Icon } from '@chilipiper/design-system/src/new'
import { SvgInform } from '@chilipiper/icons/src/design-system'
import { isDefined } from '@chilipiper/utils'
import { parse } from '@chilipiper/date-time'
import { workingHoursToEvents } from '@/components/availability-schedules/workingHoursToEvents'
import {
  defaultWorkingHours,
  findOverlappingStrictReservedWorkingHours,
} from '@/components/availability-schedules/utils'
import { useAvailabilitiesWorkingHours } from '@/components/availability-schedules/AvailabilitiesWorkingHoursContext'
import {
  addDurationToTimeString,
  formatTimeRange,
  sortByDayOrder,
} from '@/components/availability-schedules/working-hours/utils'
import { WorkingHoursTable } from '@/components/availability-schedules/working-hours/WorkingHoursTable'
import { AvailabilitySchedule, TypedScheduleDraftView } from '@/api/user-availability'

interface Props {
  isStrict: boolean
  onChange: (value: WorkingHours) => void
  scheduleId: string
  labelPosition?: 'hidden' | 'top'
  activeOn?: ActiveRange
}

const AvailabilityFormWorkingHoursInner = ({
  scheduleId,
  isStrict,
  onChange,
  labelPosition,
  activeOn,
}: Props) => {
  const labelId = useId()
  const { value: workingHoursFormValue } = useInputField('workingHours')
  const workingHoursFormValueCopy = structuredClone(workingHoursFormValue) as WorkingHours

  const events = workingHoursToEvents(scheduleId, workingHoursFormValueCopy)
  const availabilitiesWorkingHours = useAvailabilitiesWorkingHours()
  const strictReservedWorkingHours = events
    .flatMap(event => {
      return (
        availabilitiesWorkingHours &&
        findOverlappingStrictReservedWorkingHours(
          event,
          isStrict,
          availabilitiesWorkingHours,
          activeOn
        ).filter(item => !item.isConflictCausedByEvent)
      )
    })
    .filter(isDefined)
  const uniqueStrictReservedWorkingHours = R.uniqueWith(
    strictReservedWorkingHours,
    (a, b) => a.day === b.day && R.isDeepEqual(a?.result.value, b?.result.value)
  )

  const sorted = sortByDayOrder(workingHoursFormValueCopy)
  return (
    <Flex flexGrow={1} height='100%' overflow='auto' justifyContent='start' flexDirection='column'>
      <Flex flexDirection='column' gap={4}>
        {labelPosition === 'top' ? (
          <Text textStyle='form-label' color='fg/major' id={labelId}>
            Weekly Working Hours
          </Text>
        ) : (
          <ScreenReaderOnly id={labelId}>Weekly Working Hours</ScreenReaderOnly>
        )}
        <Flex flexDirection='column' boxShadow='border/section' borderRadius='12px'>
          {uniqueStrictReservedWorkingHours.length > 0 && (
            <Flex
              bg='bg/inform'
              borderTopLeftRadius='12px'
              borderTopRightRadius='12px'
              py={4}
              pr={4}
              gap={3}
              pl={6}
            >
              <Icon flexShrink={0} mt={1} icon={SvgInform} color='fg/inform' />
              <Flex flexDirection='column' gap={1}>
                <Text textStyle='body-multiline' color='fg/major'>
                  Conflicting Hours
                </Text>
                <Text textStyle='body-multiline' color='fg/moderate'>
                  The hours below could be affected by other availabilities. Remove conflicting
                  hours in the affected availability in order to have them here.
                </Text>
                {uniqueStrictReservedWorkingHours.map(reserved => {
                  const endTimeString = addDurationToTimeString(
                    reserved.result.value.startsAt,
                    parseInt(reserved.result.value.duration)
                  )
                  const [startHour, startMinutes] = reserved.result.value.startsAt.split(':')
                  const [endHour, endMinutes] = endTimeString.split(':')

                  const startPickerValue = `${startHour}:${startMinutes}`
                  const endPickerValue = `${endHour}:${endMinutes}`

                  const startTimeDate = parse(startPickerValue, 'HH:mm', new Date())
                  const endTimeDate = parse(endPickerValue, 'HH:mm', new Date())
                  return (
                    <Box
                      key={`${reserved.result.origin.id}-${reserved.result.value.startsAt}`}
                      ml={3}
                    >
                      <Text textStyle='body-multiline' color='fg/moderate'>
                        <li>
                          {R.capitalize(reserved.day)} {formatTimeRange(startTimeDate, endTimeDate)}{' '}
                          in {reserved.result.origin.schedule.name}
                        </li>
                      </Text>
                    </Box>
                  )
                })}
              </Flex>
            </Flex>
          )}
          <Flex
            borderRadius='12px'
            bg='bg/section'
            width='100%'
            role='group'
            aria-labelledby={labelId}
          >
            <WorkingHoursTable workingHours={sorted} onChange={onChange} />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

type FormProps = {
  availabilitySchedule: AvailabilitySchedule
  values: TypedScheduleDraftView['draft']
  labelPosition?: 'hidden' | 'top'
}

export const AvailabilityFormWorkingHours = ({
  availabilitySchedule,
  values,
  labelPosition,
}: FormProps) => {
  return (
    <FinalField
      isEqual={(prev, curr) => R.isDeepEqual(prev, curr)}
      name='workingHours'
      initialValue={
        Object.keys(values.workingHours ?? {}).length
          ? (values.workingHours as WorkingHours)
          : defaultWorkingHours
      }
    >
      {({ input }) => {
        return (
          <AvailabilityFormWorkingHoursInner
            scheduleId={availabilitySchedule.id}
            onChange={val => {
              input.onChange(val)
            }}
            isStrict={Boolean(values.type === 'CustomSchedule' && values.isStrictlyReserved)}
            activeOn={
              values.type === 'CustomSchedule' && values.activeOn?.type
                ? (values.activeOn as ActiveRange)
                : undefined
            }
            labelPosition={labelPosition}
          />
        )
      }}
    </FinalField>
  )
}
